import { APIAuthenticationTypeEnum } from "./workflow-api-input.types";

export interface WorkflowTrigger {
  id: string;
  name: string;
  type: 'lead_created' | 'lead_updated' | 'lead_deleted' | 'lead_repeat' | 'update_lead';
  description: string;
  config?: TriggerConfig;
}

export interface TriggerConfig {
  // For update_lead trigger
  updateFields?: UpdateFieldConfig[];
}

export interface UpdateFieldConfig {
  id: string;
  field: string;
  fieldType: 'standard' | 'custom' | 'marketing_metrics';
  valueType: 'fixed' | 'reference' | 'formula';
  value: string | number | boolean;
  referenceField?: string; // For reference value type
  formula?: string; // For formula value type
}

export interface WorkflowCondition {
  id: string;
  field: string;
  fieldType: 'standard' | 'custom' | 'marketing_metrics';
  operator: 'not' | 'equals' | 'contains' | 'in' | 'not_in' | 'not_contains' | 'not_equals' | 'greater_than' | 'less_than' | 'between' | 'is_null' | 'is_not_null' | 'is_empty' | 'is_not_empty';
  value: string | number | boolean | string[];
}

export type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;

export enum WorkflowActionType {
  SEND_WATI_TEMPLATE = 'send_wati_template',
  TRIGGER_WEBHOOK = 'trigger_webhook',
  SEND_EMAIL = 'send_email',
  UPDATE_FIELD = 'update_field',
  ASSIGN_USER = 'assign_user',
  CREATE_TASK = 'create_task',
  UPDATE_LEAD = 'update_lead',
  ADD_TASK = 'add_task',
  ADD_NOTE = 'add_note',
  ROUND_ROBIN_LEAD_ASSIGNMENT = 'round_robin_for_lead_assignment',
  CAPI_INTEGRATION = 'capi_integration'
}

export type WorkflowAction =
  | { id: string; type: WorkflowActionType.SEND_WATI_TEMPLATE; name: string; description: string; config: WatiConfig }
  | { id: string; type: WorkflowActionType.TRIGGER_WEBHOOK; name: string; description: string; config: WebhookConfig }
  | { id: string; type: WorkflowActionType.SEND_EMAIL; name: string; description: string; config: Record<string, any> }
  | { id: string; type: WorkflowActionType.UPDATE_FIELD; name: string; description: string; config: Record<string, any> }
  | { id: string; type: WorkflowActionType.ASSIGN_USER; name: string; description: string; config: Record<string, any> }
  | { id: string; type: WorkflowActionType.CREATE_TASK; name: string; description: string; config: AddTaskConfig }
  | { id: string; type: WorkflowActionType.UPDATE_LEAD; name: string; description: string; config: { updateFields: UpdateFieldConfig[] } }
  | { id: string; type: WorkflowActionType.ADD_TASK; name: string; description: string; config: AddTaskConfig }
  | { id: string; type: WorkflowActionType.ADD_NOTE; name: string; description: string; config: AddNoteConfig }
  | { id: string; type: WorkflowActionType.ROUND_ROBIN_LEAD_ASSIGNMENT; name: string; description: string; config: RoundRobinAssignmentConfig }
  | { id: string; type: WorkflowActionType.CAPI_INTEGRATION; name: string; description: string; config: CapiConfig };

export interface Workflow {
  id: string;
  name: string;
  description?: string;
  module: 'lead' | 'deal' | 'task';
  trigger: WorkflowTrigger;
  conditions: WorkflowCondition[];
  conditionLogic: 'AND' | 'OR'; // Logic operator for multiple conditions
  actions: WorkflowAction[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  trigger: Omit<WorkflowTrigger, 'id'>;
  defaultConditions: Omit<WorkflowCondition, 'id'>[];
  defaultActions: DistributiveOmit<WorkflowAction, 'id'>[];
}

export interface CreateWorkflowFormData {
  name: string;
  description?: string;
  module: 'lead' | 'deal' | 'task';
  trigger: WorkflowTrigger;
  conditions: WorkflowCondition[];
  conditionLogic: 'AND' | 'OR'; // Logic operator for multiple conditions
  actions: WorkflowAction[];
}

// Webhook specific types
export interface WebhookConfig {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  url: string;
  authentication: {
    enabled: boolean;
    type: APIAuthenticationTypeEnum;
    username?: string;
    password?: string;
    token?: string;
    apiKey?: string;
    apiKeyHeader?: string;
  };
  headers: { key: string; value: string; }[];
  encoding: 'json' | 'form' | 'xml';
  body: {
    type: 'raw' | 'lead_fields' | 'custom';
    content: string;
    selectedFields: string[];
  };
}

export interface WebhookTestResponse {
  success: boolean;
  status: number;
  response: any;
  error?: string;
}

// Task specific types
export interface AddTaskConfig {
  taskType: 'CALL' | 'MAIL' | 'MEETING';
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
  timing: {
    type: 'immediate';
  };
  dueDate?: {
    type: 'immediate' | 'relative';
    relative?: {
      value: number;
      unit: 'minutes' | 'hours' | 'days' | 'weeks';
    };
  };
  markAsCompleted?: boolean;
}

// Add Note specific types
export interface AddNoteConfig {
  noteContent: string;
  isImportant?: boolean;
}

// WATI Template specific types
export interface WatiConfig {
  account: string; // WATI account selection
  templateName: string;
  parameters: WatiParameter[];
  recipientField: string; // Which lead field contains the phone number
  testPhoneNumber?: string; // For testing purposes
}

export interface WatiParameter {
  id: string;
  name: string;
  type: 'text' | 'image' | 'document' | 'video';
  valueType: 'fixed' | 'lead_field' | 'custom';
  value: string;
  leadField?: string; // If valueType is 'lead_field'
}

export interface RoundRobinAssignmentConfig {
  assignmentMode: 'users' | 'group';
  userIds: string[];
  groupId?: string;
  includeManagers?: boolean;
  priority?: 'LOW' | 'MEDIUM' | 'HIGH';
  description?: string;
}

// CAPI (Meta Conversions API) specific types
export interface CapiConfig {
  account: string; // Meta CAPI external integration id (pixel id + access token)
  eventName: string; // Meta standard event (e.g. "Lead") or a custom event name
  testEventCode?: string; // Meta Events Manager "Test Events" code
}



